import React, { JSX, useContext } from "react";
import { FC, useState, useMemo, useEffect } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { Breadcrumb, BreadcrumbTemplateEnum, Breadcrumbs, LoadingSpinner, Page, PageHeader, PageHeaderRight, PageInner, PageSection, TooltipWrapper } from "@app/shared";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { ModalLink } from "@app/shared/Modals";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QesIcon } from "@app/shared/icons/QESIcon";
import SpecialtyService from "@app/admin/manage-specialties/specialtyService";
import { AccessCriteriaModel, TemplateDetailsData, SpecialtyGroupModel, AddTemplateDetailsData, processKeyDownNumericValidation, processKeyDownTextValidation, ServicingCriteriaModel, CategoryModel } from "@app/admin/manage-specialties/specialty";
import { ProviderRegionTypeEnum, ServicingAnalysisTypeEnum } from "@app/templates/templateModels";
import MultiselectDropdown, { DropdownItem } from "../../shared/checkbox-dropdown/MultiselectDropdown";
import { Roles, UserContext, UserContextProps } from "../../user";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { TimeDistanceCalculatorType, InputValidationRegExps } from "../../projects/enumerators";
import { compact } from "lodash";
import { TemplateMetadataModel } from "../manage-templates/template";
import { CalculationMethodEnum } from "../../templates/templateModels";
import { FlyoutLink } from "@app/shared/Flyouts";
import SpecialtyRollupFlyout from "./SpecialtyRollupFlyout";

interface SpecialtyGroupTableData {
  specialtyCode: JSX.Element;
  specialtyName: JSX.Element;
  specialtyDetails: JSX.Element;
  specialtyCategories: JSX.Element;
  remove: JSX.Element;
  isSelected: boolean;
  [wafflesKeyColumnName]: string;
  reorder: JSX.Element;
}

interface SpecialtyGroupDetailsTableData {
  specialtyCode: JSX.Element;
  specialtyName: JSX.Element;
  county: JSX.Element;
  passPercentage: JSX.Element;
  minProviderCount: JSX.Element;
  maxDistance: JSX.Element;
  calculationMethod: JSX.Element;
  maxTime: JSX.Element;
  customization: JSX.Element;
  inCountyServicingRequirement: JSX.Element;
  skipAccessTimeDistance: JSX.Element;
  inCountyAccessRequirement: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const specialtyGroupDetailsColumnConfig: ColumnConfig = {
  calculationMethod: {
    displayText: "Calculation Method",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-calculationMethod"
  },
  county: {
    displayText: "County",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-county",
  },
  customization: {
    displayText: "Customization",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-customization",
  },
  inCountyAccessRequirement: {
    displayText: "In-county access requirement",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-inCountyAccessRequirement",
  },
  inCountyServicingRequirement: {
    displayText: "In-county servicing requirement",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-inCountyServicingRequirement",
  },
  maxDistance: {
    displayText: "Max Distance",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-maxDistance",
  },
  maxTime: {
    displayText: "Max Time",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-maxTime",
  },
  minProvider: {
    displayText: "Min Servicing Providers",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-minProvider",
  },
  minProviderCount: {
    displayText: "Min Access Providers",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-minProviderCount",
  },
  passPercentage: {
    displayText: "Pass %",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-passPercentage",
  },
  skipAccessTimeDistance: {
    displayText: "Skip access time & distance",
    sizeContentToFit: true,
    testId: "specialtyGroupAccessCriteria-skipAccessTimeDistance",
  },
  specialtyCode: {
    displayText: "Code",
    sizeContentToFit: true,
    testId: "specialtyGroup-code",
  },
  specialtyName: {
    displayText: "Name",
    sizeContentToFit: true,
    testId: "specialtyGroup-name",
  },
};

const dropdownOptions: { key: string, name: string }[] =
  [
    {
      key: "moveUp",
      name: "Move Up",
    },
    {
      key: "moveDown",
      name: "Move Down",
    },
    {
      key: "moveTop",
      name: "Move To Top",
    },
    {
      key: "moveBottom",
      name: "Move To Bottom",
    }
  ];

const specialtyRollUpDropdownOptions: { key: string, name: string }[] =
  [
    {
      key: "moveUp",
      name: "Move Up",
    },
    {
      key: "moveDown",
      name: "Move Down",
    },
    {
      key: "moveTop",
      name: "Move To Top",
    },
    {
      key: "moveBottom",
      name: "Move To Bottom",
    },
    {
      key: "specialtyRollUp",
      name: "Edit Specialty Roll-Ups",
    }
  ];

const calculatorTypeRolesMap: { [key: string]: Roles[] } = {
  [TimeDistanceCalculatorType.Unknown.toLowerCase()]: undefined,
  [TimeDistanceCalculatorType.Quest.toLowerCase()]: undefined,
  [TimeDistanceCalculatorType.GeographyAware.toLowerCase()]: [Roles.GEOGRAPHY_AWARE_DISTANCE_USER],
  [TimeDistanceCalculatorType.GraphhopperRouting.toLowerCase()]: [Roles.ROUTING_DISTANCE_USER],
  [TimeDistanceCalculatorType.StraightLine.toLowerCase()]: [Roles.STRAIGHT_LINE_DISTANCE_USER],
};

const SpecialtyTimeDistanceCalculatorType: { key: string, name: string }[] =
  [
    {
      key: "GeographyAware",
      name: "GeographyAware - Geographic Driving Distance",
    },
    {
      key: "Quest",
      name: "Quest - Quest Estimated Driving Distance",
    },
    {
      key: "StraightLine",
      name: "StraightLine - Straight-Line Distance",
    }
  ];
const SpecialtyCalculationMethod: { key: string, name: string }[] =
  [
    {
      key: "unknown",
      name: "",
    },
    {
      key: "distance",
      name: "Distance",
    },
    {
      key: "time",
      name: "Time",
    },
    {
      key: "timeAndDistance",
      name: "Time and Distance",
    },
    {
      key: "timeOrDistance",
      name: "Time or Distance",
    }
  ];
interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<SpecialtyGroupTableData>,
}

interface SpecialtiesMatchProps {
  clientId?: string;
  templateCollectionId?: string;
  templateId?: string;
}

type SpecialtiesProps = RouteComponentProps<SpecialtiesMatchProps, undefined> & UserContextProps;

const take = 10;
export const Specialties: FC<React.PropsWithChildren<SpecialtiesProps>> = (props: SpecialtiesProps) => {
  const user = React.useContext(UserContext);
  const specialtyService = new SpecialtyService();

  const [loading, setLoading] = useState(true);
  const [loadingAccessCriteriaTable, setLoadingAccessCriteriaTable] = useState(true);
  const [specialtyGroups, setSpecialtyGroups] = useState<SpecialtyGroupTableData[]>([]);
  const [specialtyGroupDetails, setSpecialtyGroupDetails] = useState<SpecialtyGroupDetailsTableData[]>([]);
  const [specialtyCategoriesOptions, setSpecialtyCategoriesOptions] = useState<string[]>([]);
  const [templateDetails, setTemplateDetails] = useState<TemplateDetailsData>();

  //used to track changes to the edit calculations control
  const [origClosestProviderCount, setOrigClosestProviderCount] = useState(null);
  const [origCalculationMethod, setOrigCalculationMethod] = useState(null)
  const [origCalculationTypeOptions, setOrigCalculationTypeOptions] = useState<string[]>([]);
  const [isEditCalculationsDirtyFlag, setIsEditCalculationsDirtyFlag] = useState(false);
  const [isInCalculationMethodEditMode, setIsInCalculationMethodEditMode] = useState(false);

  //set selected edit calculations dropdown selected items
  const [selectedClosestProviderCount, setSelectedClosestProviderCount] = useState(null);
  const [selectedCalculationTypes, setSelectedCalculationTypes] = useState<DropdownItem<string>[]>([]);
  const [selectedCalculationMethod, setSelectedCalculationMethod] = useState(null);

  //all possible calculation types
  const [calculationTypeOptions, setCalculationTypeOptions] = useState<DropdownItem<string>[]>([]);

  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  const specialtyGroupColumnConfig: ColumnConfig = {
    edit: {
      displayText: "Edit",
      hidden: !featureFlags.has(Features.EDIT_ADEQUACY_TEMPLATE),
      noSort: true,
      sizeContentToFit: true,
      testId: "specialtyGroup-edit",
    },
    remove: {
      displayText: "Remove",
      hidden: !featureFlags.has(Features.EDIT_ADEQUACY_TEMPLATE),
      noSort: true,
      sizeContentToFit: true,
      testId: "specialtyGroup-remove",
    },
    reorder: {
      displayText: "",
      hidden: !featureFlags.has(Features.EDIT_ADEQUACY_TEMPLATE),
      noSort: true,
      sizeContentToFit: true,
      testId: "specialtyGroup-name",
    },
    specialtyCategories: {
      displayText: "Category",
      hidden: specialtyCategoriesOptions != null && specialtyCategoriesOptions.length == 0,
      sizeContentToFit: true,
      testId: "specialtyGroup-categories",
    },
    specialtyCode: {
      displayText: "Code",
      sizeContentToFit: true,
      testId: "specialtyGroup-code",
    },
    specialtyDetails: {
      displayText: "Specialty Details",
      sizeContentToFit: true,
      testId: "specialtyGroup-details",
    },
    specialtyName: {
      displayText: "Specialty Group Name",
      sizeContentToFit: true,
      testId: "specialtyGroup-name",
    }
  };
  const [totalRecords, setTotalRecords] = useState(0);
  const [numRecordsDisplayed, setNumRecordsDisplayed] = useState(take);
  const [specialtyNameFilter, setSpecialtyNameFilter] = useState("");
  const [isEdited, setIsEdited] = useState(false);
  const [pageTitle, setPageTitle] = useState("Manage Specialties");
  const [pageSettings, setPageSettings] = useState<PageSettings>(
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      }
    });

  const calculationMethodOptions = ["-", "AND", "OR"];
  const specialtyDetailsOptions = ["Required", "Informational"];

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => {
      setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take });
    },
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: specialtyGroups.length,
  }

  const breadcrumbs: (string | Breadcrumb)[] = useMemo(() => {
    const breadcrumbs: (string | Breadcrumb)[] = [];
    breadcrumbs.push('Manage Specialties');
    return breadcrumbs;
  }, [props.location.state]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const templateDetailsData: TemplateDetailsData = await specialtyService.getTemplateDetails(Number(props.match.params.templateCollectionId), props.match.params.templateId);
      checkTemplateDisplayOrders(templateDetailsData);
      setTemplateDetails(templateDetailsData);

      //keep the original edit calculations values for cancel purposes
      setOrigClosestProviderCount(templateDetailsData.closestProviderCount);
      const ctypes = createAllCalculationTypes(SpecialtyTimeDistanceCalculatorType);
      setCalculationTypeOptions(ctypes);
      setOrigCalculationTypeOptions(templateDetailsData.timeDistanceCalculatorTypes);
      const selectedCalcTypes = createCalculationTypes(templateDetailsData.timeDistanceCalculatorTypes);
      setSelectedCalculationTypes(selectedCalcTypes);
      setOrigCalculationMethod(templateDetailsData.categories?.[0]?.specialtyGroups?.[0]?.accessCriteria?.[0]?.calculationMethod);
      setSpecialtyCategoriesOptions(templateDetailsData?.categories?.filter(cat => cat.displayName != "").map(cat2 => cat2.displayName));
    }

    fetchData();
  }, []);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const data = await createSpecialtyGroupWafflesTable(undefined, false);
      setTotalRecords(data?.length);
    }
    if (templateDetails != null) {
      setPageTitle(`Manage Specialties - ${templateDetails?.displayName}`);
    }

    fetchData();
  }, [templateDetails, pageSettings]);

  useEffect(() => {
    setNumRecordsDisplayed(specialtyGroups.slice(paginationProps.skip, paginationProps.skip + take).length + paginationProps.skip)
  }, [paginationProps])

  const checkTemplateDisplayOrders = (templateDetailsData: TemplateDetailsData):TemplateDetailsData => {
    templateDetailsData.categories?.forEach((cat, catIndex) => {
      if (cat.displayOrder == 0 && catIndex != 0) {
        cat.displayOrder = catIndex + 1;
      }
      if (cat.displayOrder == 0 && catIndex == 0) {
        cat.displayOrder = 1;
      }
      cat.specialtyGroups?.forEach((sg, sgIndex) => {
        if (sg.displayOrder == 0 && sgIndex != 0) {
          sg.displayOrder = sgIndex + 1;
        }
        if (sg.displayOrder == 0 && sgIndex == 0) {
          sg.displayOrder = 1;
        }
      });
    });
    return templateDetailsData;
  }
  const createCalculationTypes = (calculationTypes: string[]): DropdownItem<string>[] => {
    //These calculation types are role restricted so check to make sure the user has the role before adding it to th the list
    const ctypes = calculationTypes.map((calcType) => {
      const calcTypeKey = calcType.toLowerCase();
      const calcTypeName = SpecialtyTimeDistanceCalculatorType.find(item => item.key.toLowerCase() === calcTypeKey)?.name;
      if (calculatorTypeRolesMap[calcTypeKey]) {
        const role = calculatorTypeRolesMap[calcTypeKey];
        if (role) {
          const userHasRole = user?.roles.includes(role[0]);
          if (userHasRole) {
            return { key: calcTypeKey, name: calcTypeName }
          } else {
            return { key: "", name: "" }
          }
        }
      } else {
        return { key: calcTypeKey, name: calcTypeName }
      }
    });
    return ctypes.filter(ct => ct.key != '');
  }

  const createAllCalculationTypes = (calculationTypes: { key: string, name: string }[]): DropdownItem<string>[] => {
    //These calculation types are role restricted so check to make sure the user has the role before adding it to th the list
    const ctypes = calculationTypes.map((calcType) => {
      const calcTypeKey = calcType.key.toLowerCase();
      const calcTypeName = calcType.name;

      if (calculatorTypeRolesMap[calcTypeKey]) {
        const role = calculatorTypeRolesMap[calcTypeKey];
        if (role) {
          const userHasRole = user?.roles.includes(role[0]);
          if (userHasRole) {
            return { key: calcTypeKey, name: calcTypeName }
          } else {
            return { key: "", name: "" }
          }
        }
      } else {
        return { key: calcTypeKey, name: calcTypeName }
      }

    });
    return ctypes.filter(ct => ct.key != '');
  }

  // below functions will be handled in another WI
  const createSpecialtyGroupDetailsWafflesTable = (data: SpecialtyGroupModel, viewOnly: boolean): SpecialtyGroupDetailsTableData[] => {
    
    const mergedArray = data.accessCriteria.map(ac => {
      let matchingItem = data.servicingCriteria.find(sc => sc.geographyId === ac.geographyId);
      if (!matchingItem) {
        matchingItem = {
          geographyId: ac.geographyId,
          servicingAnalysisType: ServicingAnalysisTypeEnum.InRegionOrCoversMember,
          servicingMinimumCount: 0,
          servicingRatio: "0:0",
        };
        data.servicingCriteria.push(matchingItem);
      }
      return { ...ac, ...matchingItem };
    });

    setLoadingAccessCriteriaTable(false);
    return mergedArray.map((rec) => {

      if (rec.servicingMinimumCount == undefined || rec.servicingMinimumCount == null) {
        rec.servicingMinimumCount = 0;
      }

      const calculationMethod = getCalculationMethodOperator(rec.calculationMethod);

      return ({
        /* eslint-disable sort-keys */
        key: data?.code + rec.geographyId,
        specialtyCode: <>{data?.code}</>,
        specialtyName: <>{data?.name}</>,
        county: <>{rec.geographyId}</>,
        passPercentage: (!viewOnly &&
          <QESTextInput
            label="Pass %"
            testId="edit-specialtyGroupAccessCriteria__passPercentage"
            defaultValue={rec.passPercentage}
            hideLabel={true}
            isValid={rec.passPercentage && rec.passPercentage <= 100 && rec.passPercentage > 0}
            unrequiredValidation={true}
            errorText={!(rec.passPercentage && rec.passPercentage <= 100) && "The pass percentage has to be less than 100."}
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow3DigitsValueLessThanOrEqual100); }}
            onChange={(e) => { handlePassPercentageChange(e.target.value, data, rec.geographyId); }} />
        ) || <>{rec.passPercentage}</>,
        minProvider: (!viewOnly &&
          <QESTextInput
            label="Min Provider"
            testId="edit-specialtyGroupAccessCriteria__minProvider"
            defaultValue={rec.servicingMinimumCount}
            hideLabel={true}
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { handleMinProviderChange(e.target.value, data, rec.geographyId); }} />
        ) || <>{rec.servicingMinimumCount}</>,
        minProviderCount: (!viewOnly &&
          <QESTextInput
            label="Min Provider Count"
            testId="edit-specialtyGroupAccessCriteria__minProviderCount"
            defaultValue={rec.minimumProviderCount}
            hideLabel={true}
            unrequiredValidation={true}
            isValid={rec.minimumProviderCount && (rec.minimumProviderCount == 1 || rec.minimumProviderCount == 2)}
            errorText={!(rec.minimumProviderCount && (rec.minimumProviderCount == 1 || rec.minimumProviderCount == 2)) && "The min. provider count must be 1 or 2."}
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllowOneDigitOnly1or2); }}
            onChange={(e) => { handleMinProviderCountChange(e.target.value, data, rec.geographyId); }} />
        ) || <>{rec.minimumProviderCount}</>,
        maxDistance: (!viewOnly &&
          <QESTextInput
            label="Max Distance"
            testId="edit-specialtyGroupAccessCriteria__maxDistance"
            defaultValue={rec.maximumDistanceAllowed}
            hideLabel={true}
            unrequiredValidation={true}
            isValid={!((calculationMethod == "-" && rec.maximumTimeAllowed <= 0 && rec.maximumDistanceAllowed <= 0) ||
              (calculationMethod == "-" && rec.maximumTimeAllowed > 0 && rec.maximumDistanceAllowed > 0) ||
              (calculationMethod != "-" && (rec.maximumTimeAllowed <= 0 || rec.maximumDistanceAllowed <= 0)))}
            errorText={maxTimeAndMaxDistanceErrorText(rec.maximumTimeAllowed, rec.maximumDistanceAllowed, calculationMethod)}
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { handleMaxDistanceChange(e.target.value, data, rec.geographyId); }}
          />
        ) || <>{rec.maximumDistanceAllowed}</>,
        calculationMethod: (!viewOnly && !!calculationMethod &&
          <>
            <QESSelect
              label="Calculation Method"
              testId="edit-specialtyGroupAccessCriteria__calculationMethod"
              hideLabel={true}
              defaultValue={calculationMethod}
              className="bg-white"
              onChange={(e) => { handleCalculationMethodChange(e.target.value, data, rec.geographyId) }}
            >
              {calculationMethodOptions.map(o => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </QESSelect>
          </>) ||
          <>{calculationMethod}</>,
        maxTime: (!viewOnly &&
          <QESTextInput
            label="Max Time"
            testId="edit-specialtyGroupAccessCriteria__maxTime"
            defaultValue={rec.maximumTimeAllowed}
            hideLabel={true}
            unrequiredValidation={true}
            isValid={!((calculationMethod == "-" && rec.maximumTimeAllowed <= 0 && rec.maximumDistanceAllowed <= 0) ||
              (calculationMethod == "-" && rec.maximumTimeAllowed > 0 && rec.maximumDistanceAllowed > 0) ||
              (calculationMethod != "-" && (rec.maximumTimeAllowed <= 0 || rec.maximumDistanceAllowed <= 0)))}
            errorText={maxTimeAndMaxDistanceErrorText(rec.maximumTimeAllowed, rec.maximumDistanceAllowed, calculationMethod)}
            onKeyDown={(e) => { processKeyDownNumericValidation(e, InputValidationRegExps.regexpOnlyAllow4Digits); }}
            onChange={(e) => { handleMaxTimeChange(e.target.value, data, rec.geographyId); }}
          />
        ) || <>{rec.maximumTimeAllowed}</>,
        customization: <>{rec.isAccessCustomization ? "Y" : "N"}</>,
        inCountyServicingRequirement: (!viewOnly &&
          <>
            <QESSelect
              label="In-county servicing rquirement"
              testId="edit-specialtyGroupAccessCriteria-inCountyServicingRequirement"
              hideLabel={true}
              className="bg-white"
              value={rec.servicingAnalysisType == ServicingAnalysisTypeEnum.InRegion ? "true" : "false"}
              onChange={(e) => { handleInCountyServicingRequirementChange(e.target.value, data, rec.geographyId) }}
            >
              <option key={"true"} value={"true"}>Yes</option>
              <option key={"false"} value={"false"}>No</option>
            </QESSelect>
          </>) ||
          <>{rec.servicingAnalysisType == ServicingAnalysisTypeEnum.InRegion ? "Yes" : "No"}</>,
        skipAccessTimeDistance: (!viewOnly &&
          <>
            <QESSelect
              label="Skip access time & distance"
              testId="edit-specialtyGroupAccessCriteria-skipAccessTimeDistance"
              hideLabel={true}
              className="bg-white"
              onChange={(e) => { handleSkipAccessTimeDistanceChange(e.target.value, data, rec.geographyId) }}
              value={rec.skipAccessTimeAndDistanceCalculations ? "true" : "false"}
            >
              <option key={"true"} value={"true"}>Yes</option>
              <option key={"false"} value={"false"}>No</option>
            </QESSelect>
          </>) ||
          <>{rec.skipAccessTimeAndDistanceCalculations ? "Yes" : "No"}</>,
        inCountyAccessRequirement: (!viewOnly &&
          <>
            <QESSelect
              label="In-county access rquirement"
              testId="edit-specialtyGroupAccessCriteria-inCountyAccessRequirement"
              hideLabel={true}
              className="bg-white"
              value={rec.providerRegionType == ProviderRegionTypeEnum.InRegion ? "true" : "false"}
              onChange={(e) => { handleInCountyAccessRequirementChange(e.target.value, data, rec.geographyId) }}
            >
              <option key={"true"} value={"true"}>Yes</option>
              <option key={"false"} value={"false"}>No</option>
            </QESSelect>
          </>) ||
          <>{rec.providerRegionType == ProviderRegionTypeEnum.InRegion ? "Yes" : "No"}</>,
      });
    })
    /* eslint-enable sort-keys */
  }

  const createSpecialtyGroupWafflesTable = async (row: SpecialtyGroupModel, viewOnly: boolean): Promise<SpecialtyGroupTableData[]> => {
    const data: TemplateDetailsData = templateDetails;
    type NewType = SpecialtyGroupModel;

    let specialtyGroupsData: NewType[] = data?.categories?.flatMap(cat => cat.specialtyGroups.flatMap(sg => ({ ...sg, categoryDisplayOrder: cat.displayOrder, categoryName: cat.displayName, name:sg.name })));

    //set the edit calculations controls selected items
    setSelectedCalculationMethod(data?.categories?.[0]?.specialtyGroups?.[0]?.accessCriteria?.[0]?.calculationMethod);
    setSelectedClosestProviderCount(data?.closestProviderCount);
    setIsInCalculationMethodEditMode(false);

    if (!!specialtyNameFilter) {
      specialtyGroupsData = specialtyGroupsData.filter(sg => sg.code.toLowerCase().includes(specialtyNameFilter.toLowerCase()) || sg.name.toLowerCase().includes(specialtyNameFilter.toLowerCase()));
    }

    if (row) {
      setLoadingAccessCriteriaTable(true);
      specialtyGroupsData = specialtyGroupsData.map(sg => ({ ...sg, isSelected: false }));
      specialtyGroupsData.find(sg => sg.code == row.code).isSelected = true;
      setSpecialtyGroupDetails(createSpecialtyGroupDetailsWafflesTable(row, viewOnly));
      let hasInvalidRow = true;
      if (row.accessCriteria.find(ac => ac.isValid === false)) {
        hasInvalidRow = false;
      }
      specialtyGroupsData.find(sg => sg.code == row.code).isValid = hasInvalidRow;
    }


    const DisplayReorder = (row: SpecialtyGroupModel) => {
      return (dropdownOptions.map((x) =>
        <NewActionMenuItem
          display={x.name}
          key={x.key}
          isSelected={false}
          onClick={() => {
            handleDisplayReorder(row, x.name);
          }}
          testId="manage-specialty__dropdown_template-option"
        />
      ))
    }

    const DisplayRollUpReorder = (row: SpecialtyGroupModel) => {
      return (
        <>
          <NewActionMenuItem
            display={specialtyRollUpDropdownOptions[0].name}
            key={specialtyRollUpDropdownOptions[0].key}
            isSelected={false}
            onClick={() => {
              handleDisplayReorder(row, specialtyRollUpDropdownOptions[0].name);
            }}
            testId="manage-specialty__dropdown_template-option-1"
          />
          <NewActionMenuItem
            display={specialtyRollUpDropdownOptions[1].name}
            key={specialtyRollUpDropdownOptions[1].key}
            isSelected={false}
            onClick={() => {
              handleDisplayReorder(row, specialtyRollUpDropdownOptions[1].name);
            }}
            testId="manage-specialty__dropdown_template-option-2"
          />
          <NewActionMenuItem
            display={specialtyRollUpDropdownOptions[2].name}
            key={specialtyRollUpDropdownOptions[2].key}
            isSelected={false}
            onClick={() => {
              handleDisplayReorder(row, specialtyRollUpDropdownOptions[2].name);
            }}
            testId="manage-specialty__dropdown_template-option-3"
          />
          <NewActionMenuItem
            display={specialtyRollUpDropdownOptions[3].name}
            key={specialtyRollUpDropdownOptions[3].key}
            isSelected={false}
            onClick={() => {
              handleDisplayReorder(row, specialtyRollUpDropdownOptions[3].name);
            }}
            testId="manage-specialty__dropdown_template-option-4"
          />
          <FlyoutLink
            content={{
              body: <SpecialtyRollupFlyout
                currentUserid={user.userId}
                selectedRow={row}
                setIsEdited={setIsEdited}
              />,
              title: "Edit Specialty Roll-Ups",
            }}
            testId="edit-specialty__rollup-flyout"
            classNames="specialty-rollup-flyout qes-button-secondary"
          >
            Edit Specialty Roll-Ups
          </FlyoutLink>
        </>
      )
    }

    const handleDisplayReorder = (row: SpecialtyGroupModel, operation: string) => {
      //move up means decrease the current row displayorder and increase the row before it by 1
      if (operation === 'Move Up' && (row.displayOrder !== 1)) {
        const rowCurrent = data.categories?.find(cat => cat.displayOrder == row.categoryDisplayOrder)?.specialtyGroups.find(sg => sg.displayOrder == row.displayOrder);
        const rowToIncrease = data.categories?.find(cat => cat.displayOrder == row.categoryDisplayOrder)?.specialtyGroups.find(sg => sg.displayOrder == (row.displayOrder - 1));
        rowToIncrease.displayOrder = row.displayOrder;
        rowCurrent.displayOrder = row.displayOrder - 1;
        rowCurrent.isSelected = false;
        rowCurrent.isSelected = false;
        createSpecialtyGroupWafflesTable(null, true);
      }
      //move down means increase the current row displayOrder and decrease the row after it by 1
      if (operation === 'Move Down' && (row.displayOrder !== specialtyGroupsData.length + 1)) {
        const rowCurrent = data.categories?.find(cat => cat.displayOrder == row.categoryDisplayOrder)?.specialtyGroups.find(sg => sg.displayOrder == row.displayOrder);
        const rowToDecrease = data.categories?.find(cat => cat.displayOrder == row.categoryDisplayOrder)?.specialtyGroups.find(sg => sg.displayOrder == (row.displayOrder + 1));
        rowToDecrease.displayOrder = row.displayOrder;
        rowCurrent.displayOrder = row.displayOrder + 1;
        rowCurrent.isSelected = false;
        rowCurrent.isSelected = false;
        createSpecialtyGroupWafflesTable(null, true);
      }
      if (operation === 'Move To Top' && (row.displayOrder !== 1)) {
        const rowCurrent = data.categories?.find(cat => cat.displayOrder == row.categoryDisplayOrder)?.specialtyGroups.find(sg => sg.displayOrder == row.displayOrder);
        data.categories?.find(cat => (cat.displayOrder == row.categoryDisplayOrder))?.specialtyGroups?.forEach(sg => {
          if (sg.displayOrder < row.displayOrder) {
            sg.displayOrder = sg.displayOrder + 1;
          }
        });
        rowCurrent.displayOrder = 1;
        rowCurrent.isSelected = false;
        createSpecialtyGroupWafflesTable(null, true);
      }
      if (operation === 'Move To Bottom' && (row.displayOrder !== specialtyGroupsData.length + 1)) {
        const rowCurrent = data.categories?.find(cat => cat.displayOrder == row.categoryDisplayOrder)?.specialtyGroups.find(sg => sg.displayOrder == row.displayOrder);
        data.categories?.find(cat => (cat.displayOrder == row.categoryDisplayOrder))?.specialtyGroups?.forEach(sg => {
          if (sg.displayOrder > row.displayOrder) {
            sg.displayOrder = sg.displayOrder - 1;
          }
        });
        rowCurrent.displayOrder = data.categories?.find(cat => (cat.displayOrder == row.categoryDisplayOrder))?.specialtyGroups.length + 1;
        rowCurrent.isSelected = false;
        createSpecialtyGroupWafflesTable(null, true);
      }
      setIsEdited(true);
    };

    const anySelection = !specialtyGroupsData?.every(sg => !sg.isSelected);
    const rowData = specialtyGroupsData.sort((a, b) => (a.categoryDisplayOrder - b.categoryDisplayOrder) || (a.displayOrder - b.displayOrder)).map(rec => {
      /* eslint-disable sort-keys */
      return ({
        key: rec.code.toString(),
        specialtyName: ((rec.isSelected && !viewOnly &&
          <QESTextInput
            label="specialtyGroup-name"
            testId="specialtyGroup-name"
            defaultValue={rec.name}
            hideLabel={true}
            isValid={rec.isNameValid || rec.isNameValid === undefined}
            descriptiveText={!(rec.isNameValid || rec.isNameValid === undefined) && "A specialty group with this name already exists."}
            unrequiredValidation={true}
            onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName); }}
            onChange={(e) => handleNameChange(e.target.value, rec)}
          />) ||
          <QESButton
            qesType="secondary"
            qesTestId="select-specialtyGroup__select-button"
            onClick={() => createSpecialtyGroupWafflesTable(rec, true)}
            disabled={anySelection && !viewOnly}
          >
            {rec.name} {(rec.specialties.length > 1) && <QesIcon iconName={QesIcons.FontAwesome.EllipsisVertical} />}
          </QESButton>),
        specialtyCode: ((rec.isSelected && !viewOnly &&
          <QESTextInput
            label="specialtyGroup-code"
            testId="specialtyGroup-code"
            defaultValue={rec.code}
            hideLabel={true}
            onKeyDown={(e) => { processKeyDownTextValidation(e, InputValidationRegExps.regexpExcludeSpecialCharactersForName); }}
            onChange={(e) => handleCodeChange(e.target.value, rec)}
          />) || <>{rec.code.toString()}</>),
        specialtyDetails: ((rec.isSelected && !viewOnly &&
          <QESSelect
            label="specialtyGroup-details"
            testId="specialtyGroup-details"
            hideLabel={true}
            defaultValue={getDetailsDisplayText(rec.analysisProperties)}
            className="bg-white"
            onChange={(e) => { handleDetailsChange(e.target.value, rec) }}
          >
            {specialtyDetailsOptions.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </QESSelect>
        ) || <>{getDetailsDisplayText(rec.analysisProperties)}</>),
        specialtyCategories: ((rec.isSelected && !viewOnly && (specialtyCategoriesOptions?.length > 0) &&
          <QESSelect
            label="specialtyGroup-categories"
            testId="specialtyGroup-categories"
            hideLabel={true}
            defaultValue={rec.categoryName}
            className="bg-white"
            onChange={(e) => { handleCategoriesChange(e.target.value, rec) }}
          >
            {specialtyCategoriesOptions.map(o => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </QESSelect>
        ) || <>{rec.categoryName}</>),
        edit: ((rec.isSelected && !viewOnly &&
          <QESButton
            qesType="secondary"
            qesTestId="edit-specialtyGroup__edit-button"
            onClick={() => handleSpecialtyGroupSave(rec)}
            disabled={!(rec.isValid || rec.isValid === undefined) || !(rec.isNameValid || rec.isNameValid === undefined)}
          >
            Save
          </QESButton>) || (!viewOnly && anySelection &&
            <QESButton
              qesType="secondary"
              qesTestId="edit-specialtyGroup__edit-button"
              disabled={true}
            >
              <QesIcon iconName={QesIcons.FontAwesome.Edit} />
            </QESButton>) || (
            <QESButton
              qesType="secondary"
              qesTestId="edit-specialtyGroup__edit-button"
              onClick={() => createSpecialtyGroupWafflesTable(rec, false)}
            >
              <QesIcon iconName={QesIcons.FontAwesome.Edit} />
            </QESButton>)),
        remove:
          <ModalLink
            classNames="qes-button-secondary danger"
            content={{
              body: "Are you sure you want to remove this specialty group?",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => removeSpecialtyGroup(rec),
              confirmButtonText: "Remove",
              confirmButtonType: "primary-danger",
              title: `Remove Specialty Group`,
            }}
            testId="manage-specialtyGroup__remove-specialtyGroup-modal"
            hideBodyScroll={true}
            disabled={anySelection && !viewOnly}
          >
            Remove
          </ModalLink>
        ,
        reorder: (rec.specialties.length === 1 &&
          <NewActionMenu
            testId="hideme"
            buttonText={'...'}
            containerClassnames="col-8"
            buttonClassNames="qes-button-secondary"
            closeOnClick={true}
            fixMenu={true}
            fixedMenuOffset={{
              x: -60,
              y: 10
            }}
            hideArrow={true}
          >
            {DisplayReorder(rec)}
          </NewActionMenu> ||
          <NewActionMenu
            testId="hideme"
            buttonText={'...'}
            containerClassnames="col-8 reorder-dropdown"
            buttonClassNames="qes-button-secondary"
            closeOnClick={true}
            fixMenu={true}
            fixedMenuOffset={{
              x: -60,
              y: 10
            }}
            hideArrow={true}
          >
            {DisplayRollUpReorder(rec)}
          </NewActionMenu>
        )
        ,
        isNameValid: rec.isNameValid,
        isSelected: rec.isSelected,
        isValid: rec.isValid
      });
      /* eslint-enable sort-keys */
    });
    setSpecialtyGroups(rowData);
    setLoading(false);
    return rowData;
  }

  const rowDecorator = (row: SpecialtyGroupTableData) => {
    return row.isSelected ? "info muted" : "";
  }

  const getCalculationMethodOperator = (calculationMethod: string) => {
    switch (calculationMethod) {
      case "timeAndDistance":
        return "AND";
      case "timeOrDistance":
        return "OR";
      default:
        return "-";
    }
  }

  const maxTimeAndMaxDistanceErrorText = (maxTime: number, maxDistance: number, calculationMethod: string) => {
    if (calculationMethod == "-" && maxTime <= 0 && maxDistance <= 0) {
      return "If calculation method is '-', then either maximum distance or maximum time is required.";
    }
    else if (calculationMethod == "-" && maxTime > 0 && maxDistance > 0) {
      return "If calculation method is '-', then either maximum distance or maximum time is required.";
    }
    else if (calculationMethod != "-" && (maxTime <= 0 || maxDistance <= 0)) {
      return "If calculation method is 'or' or 'and', both maximum distance and maximum time are required.";
    }
    return "";
  }

  const validateDetailsRowOnChange = (geographyId: string, specialtyGroupData: SpecialtyGroupModel) => {
    const accessCriteria: AccessCriteriaModel = specialtyGroupData.accessCriteria.find(ac => ac.geographyId == geographyId);
    const calculationMethod = getCalculationMethodOperator(specialtyGroupData.accessCriteria.find(ac => ac.geographyId == geographyId).calculationMethod);
    if (calculationMethod !== "-") {
      accessCriteria.isValid = !!accessCriteria.maximumTimeAllowed && !!accessCriteria.maximumDistanceAllowed;
    } else {
      const validMaxTime = accessCriteria.maximumTimeAllowed > 0 && (!accessCriteria.maximumDistanceAllowed || accessCriteria.maximumDistanceAllowed == 0);
      const validMaxDistance = accessCriteria.maximumDistanceAllowed > 0 && (!accessCriteria.maximumTimeAllowed || accessCriteria.maximumTimeAllowed == 0);
      accessCriteria.isValid = validMaxTime || validMaxDistance;
    }

    specialtyGroupData.accessCriteria.forEach(ac => {
      if (ac.geographyId === geographyId) {
        ac = accessCriteria;
      }
    });
    createSpecialtyGroupWafflesTable(specialtyGroupData, false);
  }

  const removeSpecialtyGroup = (groupToRemove: SpecialtyGroupModel) => {
    setIsEdited(true);
    templateDetails.categories?.map(cat => {
      cat.specialtyGroups = cat.specialtyGroups.filter(group => group.name !== groupToRemove.name && group.code !== groupToRemove.code);
    });
    createSpecialtyGroupWafflesTable(null, true);
  }

  const handleCodeChange = (e: string, selectedSpecialtyGroup: SpecialtyGroupModel) => {
    const specialtyGroup: SpecialtyGroupModel = templateDetails.categories?.flatMap(cat => cat.specialtyGroups).find(sg => sg.name === selectedSpecialtyGroup.name && sg.code === selectedSpecialtyGroup.code);
    selectedSpecialtyGroup.code = e.trim();
    specialtyGroup.code = selectedSpecialtyGroup.code;
  }

  const handleDetailsChange = (e: string, selectedSpecialtyGroup: SpecialtyGroupModel) => {
    const specialtyGroup: SpecialtyGroupModel = templateDetails.categories?.flatMap(cat => cat.specialtyGroups).find(sg => sg.name === selectedSpecialtyGroup.name && sg.code === selectedSpecialtyGroup.code);
    selectedSpecialtyGroup.analysisProperties = (e.toLowerCase() === "required" ? "none" : "informationalonly");
    specialtyGroup.analysisProperties = selectedSpecialtyGroup.analysisProperties;
  }

  const handleCategoriesChange = (e: string, selectedSpecialtyGroup: SpecialtyGroupModel) => {
    const currentSpecialtyCategory: CategoryModel = templateDetails.categories.find(cat => cat.displayName == selectedSpecialtyGroup.categoryName);
    const newSpecialtyCategory: CategoryModel = templateDetails.categories.find(cat => cat.displayName == e);
    const idx = currentSpecialtyCategory.specialtyGroups.findIndex(sg => sg.name === selectedSpecialtyGroup.name && sg.code === selectedSpecialtyGroup.code);
    selectedSpecialtyGroup.categoryName = (e);
    //remove from curent category
    currentSpecialtyCategory.specialtyGroups.splice(idx, 1);
    //add to the newly selected category
    newSpecialtyCategory.specialtyGroups.push(selectedSpecialtyGroup);
  }
  
  const handleNameChange = (e: string, selectedSpecialtyGroup: SpecialtyGroupModel) => {
    const specialtyGroups: SpecialtyGroupModel[] = templateDetails.categories?.flatMap(cat => cat.specialtyGroups);
    const specialtyGroup: SpecialtyGroupModel = specialtyGroups.find(sg => sg.name === selectedSpecialtyGroup.name && sg.code === selectedSpecialtyGroup.code);
    const nameConflicts: SpecialtyGroupModel[] = specialtyGroups.filter(sg => sg.name === e);
    selectedSpecialtyGroup.name = e.trim();;
    selectedSpecialtyGroup.isNameValid = nameConflicts.length === 0;
    selectedSpecialtyGroup.isValid = selectedSpecialtyGroup.isValid && selectedSpecialtyGroup.isNameValid;
    specialtyGroup.name = selectedSpecialtyGroup.name;
    specialtyGroup.isValid = selectedSpecialtyGroup.isValid
    specialtyGroup.isNameValid = selectedSpecialtyGroup.isNameValid
  }

  const handleSpecialtyGroupSave = (selectedSpecialtyGroup: SpecialtyGroupModel) => {
    setIsEdited(true);
    setSpecialtyGroupDetails([]);
    selectedSpecialtyGroup.isSelected = false;
    createSpecialtyGroupWafflesTable(null, true);
  }

  const handleMinProviderChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    if (specialtyGroupData.servicingCriteria.find(sc => sc.geographyId === geographyId)) {
      specialtyGroupData.servicingCriteria.find(sc => sc.geographyId === geographyId).servicingMinimumCount = Number(e);
    }
    else {
      const newServicingCriteriaRow: ServicingCriteriaModel = {
        geographyId: geographyId,
        servicingAnalysisType: templateDetails.servicingAnalysisType,
        servicingMinimumCount: Number(e),
        servicingRatio: "0:0"
      }
      specialtyGroupData.servicingCriteria.push(newServicingCriteriaRow);
    }
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handleMinProviderCountChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    specialtyGroupData.accessCriteria.find(ac => ac.geographyId === geographyId).minimumProviderCount = Number(e);
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handlePassPercentageChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    specialtyGroupData.accessCriteria.find(ac => ac.geographyId === geographyId).passPercentage = Number(e);
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handleMaxTimeChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    let maximumTimeAllowed;
    if (e.length === 0 || !Number(e)) {
      maximumTimeAllowed = 0;
    } else {
      maximumTimeAllowed = Number(e);
    }
    specialtyGroupData.accessCriteria.find(ac => ac.geographyId === geographyId).maximumTimeAllowed = maximumTimeAllowed;
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handleMaxDistanceChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    let maximumDistanceAllowed;
    if (e.length === 0 || !Number(e)) {
      maximumDistanceAllowed = 0;
    } else {
      maximumDistanceAllowed = Number(e);
    }
    specialtyGroupData.accessCriteria.find(ac => ac.geographyId === geographyId).maximumDistanceAllowed = maximumDistanceAllowed;
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handleCalculationMethodChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    let calculationMethod;
    const accessCriteria = specialtyGroupData.accessCriteria.find(ac => ac.geographyId === geographyId);
    switch (e) {
      case "AND":
        calculationMethod = CalculationMethodEnum.TimeAndDistance;
        break;
      case "OR":
        calculationMethod = CalculationMethodEnum.TimeOrDistance;
        break;
      case "-":
        if (accessCriteria.maximumDistanceAllowed > 0 && (!accessCriteria.maximumTimeAllowed || accessCriteria.maximumTimeAllowed == 0)) {
          calculationMethod = CalculationMethodEnum.Distance;
        } else if (accessCriteria.maximumTimeAllowed > 0 && (!accessCriteria.maximumDistanceAllowed || accessCriteria.maximumDistanceAllowed == 0)) {
          calculationMethod = CalculationMethodEnum.Time;
        } else {
          calculationMethod = CalculationMethodEnum.Unknown;
        }
        break;
      default:
        calculationMethod = CalculationMethodEnum.Unknown;
    }

    specialtyGroupData.accessCriteria.find(ac => ac.geographyId == geographyId).calculationMethod = calculationMethod;
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handleInCountyServicingRequirementChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    const value = e == "true" ? true : false;
    const reqChange = value ? ServicingAnalysisTypeEnum.InRegion : ServicingAnalysisTypeEnum.InRegionOrCoversMember;

    specialtyGroupData.servicingCriteria.find(ac => ac.geographyId == geographyId).servicingAnalysisType = reqChange;
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handleSkipAccessTimeDistanceChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    const value = e == "true" ? true : false;

    specialtyGroupData.accessCriteria.find(ac => ac.geographyId == geographyId).skipAccessTimeAndDistanceCalculations = value;
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handleInCountyAccessRequirementChange = (e: string, specialtyGroupData: SpecialtyGroupModel, geographyId: string) => {
    const value = e == "true" ? true : false;
    const reqChange = value ? ProviderRegionTypeEnum.InRegion : ProviderRegionTypeEnum.InRegionOrCoversMember;

    specialtyGroupData.accessCriteria.find(ac => ac.geographyId == geographyId).providerRegionType = reqChange;
    validateDetailsRowOnChange(geographyId, specialtyGroupData);
  }

  const handleClosestProviderCountChange = (e: string) => {
    const newValue = e;
    setSelectedClosestProviderCount(newValue);
    if (origClosestProviderCount != newValue && newValue != "") {
      setIsEditCalculationsDirtyFlag(true);
    }
  }

  const handleTemplateCalculationMethodChange = (e: string) => {
    const newValue = e;
    setSelectedCalculationMethod(newValue);
    if (origCalculationMethod != newValue && newValue != "") {
      setIsEditCalculationsDirtyFlag(true);
    }
  }

  const handleSort = (data: SpecialtyGroupTableData[], newSortedColumn: keyof SpecialtyGroupTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  const handleStartEditCalculations = () => {
    setIsInCalculationMethodEditMode(true);
  }

  const handleSaveEditCalculations = async () => {
    const addTemplateData = templateDetails as unknown as AddTemplateDetailsData;

    setLoadingAccessCriteriaTable(true);
    try {
      //only save the closest provider count if it is greater than zero...a 1 or a 2 should be the value
      if (Number(selectedClosestProviderCount) > 0) {
        addTemplateData.closestProviderCount = Number(selectedClosestProviderCount);
        setOrigClosestProviderCount(Number(selectedClosestProviderCount));
      }

      addTemplateData.timeDistanceCalculatorTypes = selectedCalculationTypes.map(c => c.key);

      //update all the calculation methods for a template.catgorie.specialtyGroup.accessCriteria
      if (selectedCalculationMethod != "") {
        addTemplateData?.categories?.[0]?.specialtyGroups?.map(sg => sg.accessCriteria.map(ac => ac.calculationMethod = selectedCalculationMethod));
      }

      const newTemplateData: TemplateMetadataModel = await specialtyService.addTemplateVersion(addTemplateData.templateCollectionId, addTemplateData);
      props.match.params.templateId = newTemplateData.templateId;

      setIsEdited(false);
      setSpecialtyGroupDetails([]);
      createSpecialtyGroupWafflesTable(null, true);

      setIsEditCalculationsDirtyFlag(false);
      setIsInCalculationMethodEditMode(false);
    } finally {
      setLoadingAccessCriteriaTable(false);
    }
  }

  const handleCancelEditCalculations = () => {
    setSelectedClosestProviderCount(origClosestProviderCount);
    const ctypes = createCalculationTypes(origCalculationTypeOptions);
    setSelectedCalculationTypes(ctypes);
    setSelectedCalculationMethod(origCalculationMethod);

    setIsEditCalculationsDirtyFlag(false);
    setIsInCalculationMethodEditMode(false);
  }

  const handleSearch = () => {
    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleTemplateSave = () => {
    setLoading(true);
    setIsEdited(false);
    const saveData = async () => {
      const newTemplateDetailsData: TemplateDetailsData = await specialtyService.postTemplateDetails(Number(props.match.params.templateCollectionId), templateDetails);
      props.match.params.templateId = newTemplateDetailsData.templateId;
    }
    saveData();
    createSpecialtyGroupWafflesTable(null, true);
  }

  const handleTemplateReload = () => {
    setLoading(true);
    setIsEdited(false);
    const fetchData = async () => {
      const templateDetailsData = await specialtyService.getTemplateDetails(Number(props.match.params.templateCollectionId), props.match.params.templateId)
      checkTemplateDisplayOrders(templateDetailsData);
      setTemplateDetails(templateDetailsData);      
      setSpecialtyCategoriesOptions(templateDetails?.categories?.filter(cat => cat.displayName != "").map(cat2 => cat2.displayName));
    }

    fetchData();
    createSpecialtyGroupWafflesTable(null, true);
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const clearFilter = () => {
    setSpecialtyGroupDetails([]);
    setSpecialtyNameFilter("");
    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleCalculationTypesChange = (selectedItems: DropdownItem<string>[]): void => {
    setSelectedCalculationTypes(selectedItems);
    setIsEditCalculationsDirtyFlag(true);
    setIsInCalculationMethodEditMode(true);
  }

  const getDetailsDisplayText = (e: string) => {
    return e.toLowerCase() === "informationalonly" ? "Informational" : "Required"
  }

  const volumeReportOptions = useMemo(() => {
    return compact([
      <NewActionMenuItem
        display=".JSON"
        key="json"
        isSelected={false}
        onClick={() => downloadTemplateFile(parseInt(props.match.params.templateCollectionId), props.match.params.templateId, "json")}
        testId="manage-specialty__download_template-option--json"
      />,
      <NewActionMenuItem
        display=".XLSX"
        key="xlsx"
        isSelected={false}
        onClick={() => downloadTemplateFile(parseInt(props.match.params.templateCollectionId), props.match.params.templateId, "xlsx")}
        testId="manage-specialty__download_template-option--xlsx"
      />
    ])
  }, [featureFlags]);

  const downloadTemplateFile = (templateCollectionId: number, templateId: string, fileType: string) => {
    if (fileType === "json") {
      authenticatedDownload(specialtyService.getTemplateDetailsJSONFile(templateCollectionId, templateId));
    }
    else {
      authenticatedDownload(specialtyService.getTemplateDetailsXLSXFile(templateCollectionId, templateId));
    }
  }

  return (
    <>
      <Page documentTitle="Manage Specialties - Admin">
        <Breadcrumbs
          template={BreadcrumbTemplateEnum.ManageTemplates}
          items={breadcrumbs}
        />
        <PageInner>
          <PageHeader title={pageTitle} iconName={QesIcons.FontAwesome.Folder}>
            <PageHeaderRight>
              <div className="row">
                {featureFlags.has(Features.EDIT_TAXONOMY_CODE_MAPS) &&
                  <Link className="manage-templates-filters qes-button qes-button-primary mr-3" to={{ pathname: `/admin/manage-taxonomies/${props.match.params.templateCollectionId}/${props.match.params.templateId}` }}>
                    Edit Taxonomy Code Maps
                  </Link>
                }
                {featureFlags.has(Features.EDIT_ADEQUACY_TEMPLATE) &&
                  <Link className="manage-templates-filters qes-button qes-button-primary" to={{ pathname: `/admin/manage-templates/manage-specialties/specialties/${props.match.params.templateCollectionId}/${props.match.params.templateId}/add-specialty` }}>
                    Add Specialty
                  </Link>
                }
              </div>
            </PageHeaderRight>
          </PageHeader>
          <div className="mb 2 float-right">
            <NewActionMenu
              alignRight={true}
              closeOnClick={true}
              buttonClassNames="qes-button-primary ml-3 mt-3"
              buttonText="Download"
              testId="manage-specialty__download_template-dropdown"
            >
              {volumeReportOptions}
            </NewActionMenu>
          </div>
          <PageSection>
            {featureFlags.has(Features.EDIT_ADEQUACY_TEMPLATE) &&
              <>
                <div className="row mt-3">
                  <div className="manage-templates-edit-calculations mb-3 mt-3 col-12">
                    <QESSelect
                      allowEmpty={true}
                      containerClassName="manage-templates-edit-calculations_closestprovidercount__select"
                      disabled={!isInCalculationMethodEditMode}
                      label="Closest Provider Count"
                      testId="manage-specialty-edit-calculations__manage-specialtyGroup-closestProviderCount"
                      value={selectedClosestProviderCount}
                      onChange={(e) => handleClosestProviderCountChange(e.target.value)}
                    >
                      <option key={1} value={1}>1</option>
                      <option key={2} value={2}>2</option>
                    </QESSelect>
                    <MultiselectDropdown
                      containerClassName="manage-templates-edit-calculations_calculationtype__select"
                      disabled={!isInCalculationMethodEditMode}
                      label="Calculation Type"
                      testId="manage-specialty-edit-calculations__manage-specialtyGroup-calculationtype"
                      hideSearch={true}
                      items={calculationTypeOptions}
                      onSelectionChanged={(selectedItems: DropdownItem<string>[]) => { handleCalculationTypesChange(selectedItems) }}
                      selectedItems={selectedCalculationTypes}
                      showSelectedTextNotItemsSelectedCount={true}
                    />
                    <TooltipWrapper
                      containerClassName="ml-4"
                      style={{ display: "flex" }}
                      text={"This selection will apply to all county-specialties and overwrites any existing Calculation Methods selected"}
                      position="top"
                    >
                      <QESSelect
                        allowEmpty={false}
                        containerClassName="manage-templates-edit-calculations_calculationmethod__select"
                        disabled={!isInCalculationMethodEditMode}
                        label="Calculation Method"
                        testId="manage-specialty-edit-calculations__manage-specialtyGroup-calculationmethod"
                        value={selectedCalculationMethod}
                        onChange={(e) => handleTemplateCalculationMethodChange(e.target.value)}

                      >
                        {SpecialtyCalculationMethod.map((x, index) => <option key={index} value={x.key}>{x.name}</option>)}
                      </QESSelect>
                    </TooltipWrapper>
                    <QESButton
                      qesType="secondary"
                      qesTestId="edit-specialtyGroup_calculation_type__edit-button"
                      onClick={handleStartEditCalculations}
                      style={{ display: isInCalculationMethodEditMode ? "none" : "" }}
                      className="ml-5"
                    >
                      Edit
                    </QESButton>
                    <QESButton
                      qesType="secondary"
                      qesTestId="save-specialtyGroup_calculation_type__save-button"
                      style={{ display: !isInCalculationMethodEditMode ? "none" : "" }}
                      onClick={handleSaveEditCalculations}
                      disabled={!isEditCalculationsDirtyFlag}
                      className="ml-5"
                    > Save
                    </QESButton>
                    <QESButton
                      qesType="secondary-danger"
                      qesTestId="cancel-specialtyGroup_calculation_type__cancel-button"
                      style={{ display: !isInCalculationMethodEditMode ? "none" : "" }}
                      onClick={handleCancelEditCalculations}
                    > Cancel
                    </QESButton>
                  </div>
                </div>
                <div className="manage-templates-filters mb-3 mt-3">
                  <QESTextInput
                    containerClassName="manage-templates-filters__input"
                    label="Specialty Group Name/Code"
                    testId="manage-specialty-filter__manage-specialtyGroup-specialtyGroupName"
                    value={specialtyNameFilter}
                    onKeyUp={(e) => handleSearchKeyUp(e)}
                    onChange={(e) => setSpecialtyNameFilter(e.target.value)}
                  />
                  <QESButton qesType="primary" qesTestId="manage-specialtyGroup__search-button" onClick={handleSearch}>Search</QESButton>
                  <QESButton qesType="secondary" qesTestId="manage-specialtyGroup__clear-filters" onClick={clearFilter} disabled={!!specialtyNameFilter}>Clear</QESButton>
                </div>
                <p>
                  Displaying {numRecordsDisplayed} of {totalRecords} specialties {!!specialtyNameFilter ? "(filtered)" : ""}
                  {isEdited &&
                    <div style={{ display: "inline-flex", flexDirection: "row", float: "right", justifyContent: "flex-end", paddingRight: "2%" }}>
                      <QESButton qesType="secondary" qesTestId="manage-specialtyGroup__save-button" onClick={() => handleTemplateSave()}>Save</QESButton>
                      <ModalLink
                        classNames="qes-button-secondary danger"
                        content={{
                          body: "Are you sure you want to discard your changes?",
                          cancelButtonText: "Cancel",
                          confirmButtonAction: () => handleTemplateReload(),
                          confirmButtonText: "Discard",
                          confirmButtonType: "primary-danger",
                          title: `Discard Template Edits`,
                        }}
                        testId="manage-specialtyGroup__cancel-edits-modal"
                        hideBodyScroll={true}
                      >
                        Cancel
                      </ModalLink>
                    </div>}
                </p>
              </>
            }
            {loading ? <LoadingSpinner /> :
              <div className="row mt-3">
                <div className="col-12" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }} >
                  <div style={{ minWidth: "100%", paddingLeft: "0%", paddingRight: "2%" }}>
                    <Waffles<SpecialtyGroupTableData>
                      columns={specialtyGroupColumnConfig}
                      customSort={handleSort}
                      data={specialtyGroups.slice(paginationProps.skip, paginationProps.skip + take)}
                      enablePagination={true}
                      fullWidth={true}
                      loading={loading}
                      passThroughSorting={true}
                      paginationProps={paginationProps}
                      rowDecorator={rowDecorator}
                      testId="client-manage-specialtyGroup-waffles-table" />
                  </div>
                </div>
              </div>}
            {specialtyGroupDetails.length > 0 && (loadingAccessCriteriaTable ? <LoadingSpinner /> :
              <div className="row mt-3">
                <div className="col-12" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }} >
                  <div style={{ minWidth: "100%", paddingLeft: "3%", paddingRight: "2%" }}>
                    <Waffles<SpecialtyGroupDetailsTableData>
                      columns={specialtyGroupDetailsColumnConfig}
                      data={specialtyGroupDetails}
                      enablePagination={false}
                      fullWidth={true}
                      loading={loadingAccessCriteriaTable}
                      passThroughSorting={true}
                      testId="specialty-manage-specialtyGroupAccessCriteria-waffles-table" />
                  </div>
                </div>
              </div>)}
          </PageSection>
        </PageInner>
      </Page >
    </>
  );
}
/* eslint-enable */